import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ThemeColors, ButtonConfig } from './theme.service';

export interface ThemeState {
  colors: ThemeColors;
  buttonConfigs: ButtonConfig[];
  isLoading: boolean;
  lastUpdated: Date | null;
  error: string | null;
}

const initialState: ThemeState = {
  colors: {
    primary_default: '#63CAE5',
    primary_hover: '#74D6E9',
    primary_active: '#85D9EC',
    feedback_error: '#EF4444',
    feedback_success: '#10B981',
    feedback_warning: '#F59E0B',
    gray_100: '#F3F4F6',
    gray_200: '#E5E7EB',
    gray_500: '#9CA3AF',
    gray_700: '#4B5563',
    gray_800: '#1C1C1C',
    background_white: '#F9FAFB',
    text_primary: '#1C1C1C',
    text_invert: '#FFFFFF',
    text_secondary: '#4B5563',
    text_accent: '#0D0E10',
    border_accent: '#0D0E10'
  },
  buttonConfigs: [],
  isLoading: false,
  lastUpdated: null,
  error: null
};

@Injectable({
  providedIn: 'root'
})
export class ThemeStore {
  private stateSubject = new BehaviorSubject<ThemeState>(initialState);
  public state$ = this.stateSubject.asObservable();

  constructor() {
    console.log('🏪 ThemeStore initialized with default state');
  }

  /**
   * Get current theme state
   */
  getCurrentState(): ThemeState {
    return this.stateSubject.value;
  }

  /**
   * Get current theme colors
   */
  getCurrentColors(): ThemeColors {
    return this.stateSubject.value.colors;
  }

  /**
   * Get colors as observable
   */
  getColors$(): Observable<ThemeColors> {
    return this.state$.pipe(
      map(state => state.colors)
    );
  }

  /**
   * Get button configurations as observable
   */
  getButtonConfigs$(): Observable<ButtonConfig[]> {
    return this.state$.pipe(
      map(state => state.buttonConfigs)
    );
  }

  /**
   * Get current button configurations
   */
  getCurrentButtonConfigs(): ButtonConfig[] {
    return this.stateSubject.value.buttonConfigs;
  }

  /**
   * Get button config by type
   */
  getButtonConfigByType(type: 'default' | 'ghost' | 'ghost2' | 'confirm' | 'delete'): ButtonConfig | null {
    const configs = this.getCurrentButtonConfigs();
    return configs.find(config => config.button_type === type) || null;
  }

  /**
   * Update button configurations
   */
  updateButtonConfigs(configs: ButtonConfig[]): void {
    const currentState = this.stateSubject.value;
    this.stateSubject.next({
      ...currentState,
      buttonConfigs: configs,
      lastUpdated: new Date(),
      error: null
    });

    console.log('🔘 ThemeStore: Button configs updated', configs);
  }

  /**
   * Get loading state as observable
   */
  getLoadingState$(): Observable<boolean> {
    return this.state$.pipe(
      map(state => state.isLoading)
    );
  }

  /**
   * Update theme colors
   */
  updateColors(colors: Partial<ThemeColors>): void {
    const currentState = this.stateSubject.value;
    const updatedColors = { ...currentState.colors, ...colors };

    this.stateSubject.next({
      ...currentState,
      colors: updatedColors,
      lastUpdated: new Date(),
      error: null
    });

    console.log('🎨 ThemeStore: Colors updated', updatedColors);
  }

  /**
   * Set loading state
   */
  setLoading(isLoading: boolean): void {
    const currentState = this.stateSubject.value;
    this.stateSubject.next({
      ...currentState,
      isLoading,
      error: isLoading ? null : currentState.error
    });

    console.log('⏳ ThemeStore: Loading state set to', isLoading);
  }

  /**
   * Set error state
   */
  setError(error: string): void {
    const currentState = this.stateSubject.value;
    this.stateSubject.next({
      ...currentState,
      error,
      isLoading: false
    });

    console.log('❌ ThemeStore: Error set', error);
  }

  /**
   * Clear error state
   */
  clearError(): void {
    const currentState = this.stateSubject.value;
    this.stateSubject.next({
      ...currentState,
      error: null
    });

    console.log('✅ ThemeStore: Error cleared');
  }

  /**
   * Reset to initial state
   */
  reset(): void {
    this.stateSubject.next(initialState);
    console.log('🔄 ThemeStore: Reset to initial state');
  }

  /**
   * Update a single color
   */
  updateSingleColor(key: keyof ThemeColors, value: string): void {
    const currentState = this.stateSubject.value;
    const updatedColors = { ...currentState.colors, [key]: value };

    this.stateSubject.next({
      ...currentState,
      colors: updatedColors,
      lastUpdated: new Date(),
      error: null
    });

    console.log(`🎨 ThemeStore: Single color updated - ${key}: ${value}`);
  }

  /**
   * Check if colors have been modified from defaults
   */
  hasModifiedColors(): boolean {
    const currentColors = this.getCurrentColors();
    const defaultColors = initialState.colors;

    return Object.keys(currentColors).some(key =>
      currentColors[key as keyof ThemeColors] !== defaultColors[key as keyof ThemeColors]
    );
  }
}

